import { useEffect, useRef, useState } from "react";

export async function playerCodeDestination(code: string, hostname = location.hostname): Promise<string> {
  const value = code.trim();
  if (!value || value.length > 4096 || /\s/.test(value)) throw new Error("invalid_code");
  // Discover only this company's registered controller. Never send the code to cloud.
  const response = await fetch(`/api/v1/pos-controller-discovery?hostname=${encodeURIComponent(hostname)}&nonce=${crypto.randomUUID()}`, { cache: "no-store", credentials: "omit", signal: AbortSignal.timeout(8000) });
  if (!response.ok) throw new Error("controller_unavailable");
  const data = await response.json();
  if (data.hostname !== hostname || typeof data.endpoint !== "string") throw new Error("controller_unavailable");
  const url = new URL(data.endpoint);
  if (url.protocol !== "https:" || url.username || url.password || url.search || url.hash || (url.pathname !== "/" && url.pathname !== "")) throw new Error("controller_unavailable");
  if (typeof data.hall_id !== "string" || !data.hall_id) throw new Error("controller_unavailable");
  try {
    const check = await fetch(new URL("/v1/local/network-check", url).href, {
      method: "GET", mode: "cors", credentials: "omit", cache: "no-store",
      redirect: "error", signal: AbortSignal.timeout(8000),
    });
    if (!check.ok) throw new Error("hall_network_required");
    const presence = await check.json();
    if (presence.status !== "inside_hall" || presence.hall_id !== data.hall_id) throw new Error("hall_network_required");
  } catch {
    throw new Error("hall_network_required");
  }
  url.pathname = "/player/";
  // Fragments stay in the browser, out of HTTP requests and access logs.
  url.hash = new URLSearchParams({ claim: value }).toString();
  return url.href;
}

export function HallCodeDialog({ es, onClose }: { es: boolean; onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const active = useRef(true);
  const text = (en: string, spanish: string) => es ? spanish : en;
  useEffect(() => { active.current = true; dialog.current?.showModal(); return () => { active.current = false; }; }, []);
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true); setError("");
    try {
      const destination = await playerCodeDestination(code);
      if (active.current) location.assign(destination);
    } catch (failure) {
      if (active.current) {
        setError(failure instanceof Error && failure.message === "invalid_code"
          ? text("Enter the code exactly as printed on your receipt, without spaces.", "Introduce el código del recibo exactamente, sin espacios.")
          : failure instanceof Error && failure.message === "hall_network_required"
            ? text("Connect to the hall’s Wi-Fi to load your cards and play, then press Continue again. If you’re already connected, allow local-network access if your browser asks, or ask hall staff to check the controller.", "Conéctate al Wi-Fi de la sala para cargar tus cartones y jugar; después pulsa Continuar. Si ya estás conectado, permite el acceso a la red local si el navegador lo solicita o consulta al personal.")
            : text("We couldn’t find this hall’s player connection. Please try again or ask hall staff.", "No encontramos la conexión de esta sala. Reintenta o consulta al personal."));
        setBusy(false);
      }
    }
  }
  return <dialog ref={dialog} className="cl-code-dialog" aria-labelledby="hall-code-title" onCancel={onClose} onClose={onClose}>
    <div className="cl-code-heading"><h2 id="hall-code-title">{text("Enter your hall code", "Introduce tu código de sala")}</h2><button type="button" onClick={onClose} aria-label={text("Close", "Cerrar")}>×</button></div>
    <p>{text("Use the code issued by hall staff. If you have a QR, scan it with your phone’s camera.", "Usa el código del personal. Si tienes un QR, escanéalo con la cámara de tu teléfono.")}</p>
    <form onSubmit={submit}>
      <label htmlFor="hall-player-code">{text("Your code", "Tu código")}</label>
      <input id="hall-player-code" value={code} onChange={event => setCode(event.target.value)} placeholder={text("Enter code", "Introduce el código")} required maxLength={4096} autoFocus autoComplete="off" autoCapitalize="none" spellCheck={false} disabled={busy} aria-describedby={error ? "hall-code-error" : undefined}/>
      {error && <p id="hall-code-error" role="alert">{error}</p>}
      <button className="cl-cta" type="submit" disabled={busy}>{busy ? text("Checking hall Wi-Fi…", "Comprobando Wi-Fi…") : text("Continue", "Continuar")} <span aria-hidden="true">→</span></button>
    </form>
  </dialog>;
}
